import { useState } from 'react';
import { EstageLogo } from './EstageLogo';
import { track } from '@/lib/analytics';

interface NavbarProps {
  onCtaClick: () => void;
  onLoginClick: () => void;
  onBlogClick?: () => void;
  /**
   * Optional handler invoked when a nav link or the logo is clicked.
   * - Called with a section hash (e.g. `#features`) for nav links.
   * - Called with no argument for logo (= scroll-to-top on landing,
   *   navigate-home elsewhere).
   * When omitted, Navbar falls back to same-page smooth scrolling —
   * use this form on the landing page itself. On other pages, pass a
   * handler that navigates to `/` and optionally scrolls to the hash.
   */
  onNavigateHome?: (sectionHash?: string) => void;
}

const sectionLinks = [
  { label: 'Features', id: '#features' },
  { label: 'So funktioniert es', id: '#process' },
  { label: 'Preise', id: '#pricing' },
  { label: 'FAQ', id: '#faq' },
];

export const Navbar: React.FC<NavbarProps> = ({ onCtaClick, onLoginClick, onBlogClick, onNavigateHome }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSection = (hash: string) => {
    if (onNavigateHome) {
      onNavigateHome(hash);
    } else {
      document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const handleLogo = () => {
    if (onNavigateHome) {
      onNavigateHome();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <>
      <nav className="lp-nav">
        <div className="lp-nav-container">
          <a href="/" className="lp-nav-logo" onClick={(e) => { e.preventDefault(); handleLogo(); }}>
            <EstageLogo />
          </a>
          <div className="lp-nav-links">
            {sectionLinks.map(link => (
              <a key={link.id} href={`/${link.id}`} onClick={(e) => { e.preventDefault(); handleSection(link.id); }}>
                {link.label}
              </a>
            ))}
            {onBlogClick && (
              <a href="/blog" onClick={(e) => { e.preventDefault(); onBlogClick(); }}>Blog</a>
            )}
          </div>
          <div className="lp-nav-actions">
            <button className="lp-btn-login" onClick={() => { track('Landing Login Clicked'); onLoginClick(); }}>Anmelden</button>
            <button className="lp-btn-nav-cta" onClick={() => { track('Landing CTA Clicked', { location: 'navbar' }); onCtaClick(); }}>
              Jetzt kostenlos testen
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"/>
              </svg>
            </button>
          </div>
          <div className="lp-nav-mobile-actions">
            <button
              className="lp-btn-nav-cta mobile-compact"
              onClick={() => { track('Landing CTA Clicked', { location: 'navbar_mobile' }); onCtaClick(); }}
            >
              Jetzt kostenlos testen
            </button>
            <button
              className={`lp-nav-burger${mobileOpen ? ' open' : ''}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menü öffnen"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`lp-mobile-menu${mobileOpen ? ' open' : ''}`}>
        <div className="lp-mobile-menu-header">
          <a href="/" className="lp-mobile-menu-logo" onClick={(e) => { e.preventDefault(); handleLogo(); }}>
            <EstageLogo />
          </a>
          <button className="lp-mobile-menu-close" onClick={() => setMobileOpen(false)} aria-label="Schließen">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <nav className="lp-mobile-menu-nav">
          {sectionLinks.map(link => (
            <a key={link.id} href={`/${link.id}`} onClick={(e) => { e.preventDefault(); handleSection(link.id); }}>
              {link.label}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
              </svg>
            </a>
          ))}
          {onBlogClick && (
            <a href="/blog" onClick={(e) => { e.preventDefault(); setMobileOpen(false); onBlogClick(); }}>
              Blog
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
              </svg>
            </a>
          )}
        </nav>
        <div className="lp-mobile-menu-footer">
          <button className="lp-btn-primary" onClick={() => { setMobileOpen(false); track('Landing CTA Clicked', { location: 'mobile_menu' }); onCtaClick(); }}>
            Jetzt kostenlos testen
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"/>
            </svg>
          </button>
          <div className="lp-mobile-menu-login">
            Hast du schon ein Konto?{' '}
            <button style={{ color: 'var(--color-brand)', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }} onClick={() => { setMobileOpen(false); onLoginClick(); }}>
              Anmelden
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
